import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { FaBullseye, FaShieldAlt, FaLightbulb, FaUsers } from "react-icons/fa";
import { selectUser } from "../../store/slice/user";
import { selectCountries } from "../../store/slice/countries";
import SubPageNav from "../../components/SubPageNav";
import SiteFooter from "../../components/SiteFooter";
import { IMAGES } from "../../theme";
import { getStaticPage } from "../../services/data/staticPages";
import { Style } from "./style";

const valueIcons = [<FaBullseye />, <FaShieldAlt />, <FaLightbulb />, <FaUsers />];

// Mirrors new-design/o7events-frontend/about.html: hero, Our Story,
// Our Values (4 cards), Leadership Team, Join Our Journey CTA. Content is
// backend-driven (admin "Static Pages" > About) with the original hardcoded
// copy kept as a fallback so the page never breaks/flashes empty while the
// backend content is being filled in.
const About = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language, countryId } = useSelector(selectUser);
  const countries = useSelector(selectCountries);
  const isArabic = language === "ar";

  const activeCountry = countries.find((c) => c?.id === countryId);
  const region =
    activeCountry?.translation?.[language]?.name || t("marketing.nav.everywhere");

  const [apiData, setApiData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getStaticPage("about")
      .then((res) => {
        if (!cancelled) setApiData(res.data.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const tr = (item, field) =>
    item?.translation?.[language]?.[field] || item?.translation?.en?.[field] || null;

  const heroItem = apiData?.hero?.[0];
  const heroTitle = tr(heroItem, "title") || t("marketing.about.title");
  const heroTagline = tr(heroItem, "subtitle") || t("marketing.about.tagline");

  const storyItem = apiData?.story?.[0];
  const storyBody = tr(storyItem, "body");
  const storyParagraphs = storyBody
    ? storyBody.split(/\n\s*\n/).filter(Boolean)
    : [
        t("marketing.about.storyP1", { region }),
        t("marketing.about.storyP2"),
        t("marketing.about.storyP3"),
      ];

  const hardcodedValues = [
    { icon: <FaBullseye />, title: t("marketing.about.valueUserFirst"), body: t("marketing.about.valueUserFirstBody") },
    { icon: <FaShieldAlt />, title: t("marketing.about.valueTrust"), body: t("marketing.about.valueTrustBody") },
    { icon: <FaLightbulb />, title: t("marketing.about.valueInnovation"), body: t("marketing.about.valueInnovationBody") },
    { icon: <FaUsers />, title: t("marketing.about.valueCommunity"), body: t("marketing.about.valueCommunityBody") },
  ];
  const values =
    apiData?.value?.length
      ? apiData.value.map((v, i) => ({
          icon: valueIcons[i] || valueIcons[valueIcons.length - 1],
          title: tr(v, "title"),
          body: tr(v, "body"),
        }))
      : hardcodedValues;

  // Real leadership team, matching new-design/about.html exactly (names are
  // proper nouns and stay untranslated there too — only the role is localized).
  const hardcodedTeam = [
    { photo: IMAGES.teamYousef, name: "Yousef Awad", role: t("marketing.about.ceoFounder") },
    { photo: IMAGES.teamAbdulla, name: "Abdulla Al Wazzan", role: t("marketing.about.partner") },
    { photo: IMAGES.teamOlivia, name: "Olivia Armstrong", role: t("marketing.about.publicRelations") },
  ];
  const teamPhotoFallbacks = [IMAGES.teamYousef, IMAGES.teamAbdulla, IMAGES.teamOlivia];
  const team =
    apiData?.team_member?.length
      ? apiData.team_member.map((m, i) => ({
          photo: m.full_image || teamPhotoFallbacks[i] || IMAGES.teamYousef,
          name: tr(m, "title"),
          role: tr(m, "subtitle"),
        }))
      : hardcodedTeam;

  const ctaItem = apiData?.cta?.[0];
  const ctaTitle = tr(ctaItem, "title") || t("marketing.about.joinOurJourney");
  const ctaBody = tr(ctaItem, "body") || t("marketing.about.joinOurJourneyBody");
  const ctaButtonLabel = tr(ctaItem, "button_label") || t("marketing.about.viewOpenPositions");

  return (
    <Box sx={Style.page}>
      <SubPageNav />
      <Box sx={Style.hero}>
        <Typography component="h1" sx={Style.heroTitle(isArabic)}>
          {heroTitle}
        </Typography>
        <Typography sx={Style.subtitle}>{heroTagline}</Typography>
      </Box>

      <Box sx={Style.section}>
        <Typography component="h2" sx={Style.sectionHeading(isArabic)}>
          {t("marketing.about.ourStory")}
        </Typography>
        {storyParagraphs.map((p, i) => (
          <Typography key={i} sx={Style.paragraph(isArabic)}>
            {p}
          </Typography>
        ))}
      </Box>

      <Box sx={Style.section}>
        <Typography component="h2" sx={Style.sectionHeading(isArabic)}>
          {t("marketing.about.ourValues")}
        </Typography>
        <Box sx={Style.values}>
          {values.map((v, i) => (
            <Box key={i} sx={Style.valueCard}>
              <Box sx={Style.valueIcon}>{v.icon}</Box>
              <Typography component="h3" sx={Style.valueTitle(isArabic)}>
                {v.title}
              </Typography>
              <Typography sx={Style.valueBody(isArabic)}>{v.body}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={Style.section}>
        <Typography component="h2" sx={Style.sectionHeading(isArabic)}>
          {t("marketing.about.leadershipTeam")}
        </Typography>
        <Box sx={Style.team}>
          {team.map((member, i) => (
            <Box key={i} sx={Style.teamMember}>
              <Box sx={Style.teamAvatar}>
                <Box
                  component="img"
                  src={member.photo}
                  alt={member.name}
                  sx={Style.teamAvatarImg}
                />
              </Box>
              <Typography component="h4" sx={Style.teamRole(isArabic)}>
                {member.name}
              </Typography>
              <Typography sx={Style.teamSub}>{member.role}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={Style.ctaSection}>
        <Typography component="h2" sx={Style.sectionHeading(isArabic)}>
          {ctaTitle}
        </Typography>
        <Typography sx={Style.ctaBody(isArabic)}>
          {ctaBody}
        </Typography>
        <Box
          component="button"
          sx={Style.ctaButton(isArabic)}
          onClick={() => navigate("/contact-sales")}
        >
          {ctaButtonLabel}
        </Box>
      </Box>
      <SiteFooter />
    </Box>
  );
};

export default About;
